import { useEffect, useMemo, useState } from 'react';
import {
  NJAccordion,
  NJAccordionItem,
  NJAccordionItemContent,
  NJAccordionItemHeader,
  NJButton,
} from '@engie-group/fluid-design-system-react';
import type { DiscoveredPrototype } from '../shared/discoverPrototypes';
import {
  CATEGORIES,
  NETWORK_GROUP,
  NETWORK_GROUP_KEY,
  NETWORK_PAGE_KEYS,
  matchesCategoryFilter,
  STATUS_FILTERS,
  autoCategoryFromMeta,
  getCategoryDef,
  normalizeCategory,
  pageKeyOf,
  subpageKeyOf,
} from './home/constants';
import type { CategoryDef, Status } from './home/constants';
import { isEditableStatus } from '../shared/prototypeStatus';
import { storage } from './home/storage';
import { useStudioState } from './home/useStudioState';
import HeroSection from './home/HeroSection';
import type { SortKey } from './home/HeroSection';
import RecentBar from './home/RecentBar';
import CategorySidebar from './home/CategorySidebar';
import PrototypeCard from './home/PrototypeCard';
import { S } from '../shared/studioTokens';

interface HomeProps {
  prototypes: DiscoveredPrototype[];
}

export default function Home({ prototypes }: HomeProps) {
  const [sort, setSort]                     = useState<SortKey>('recent');
  const [search, setSearch]                 = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeAuthors, setActiveAuthors]   = useState<string[]>([]);
  const [activeTags, setActiveTags]         = useState<string[]>([]);
  const [activeStatus, setActiveStatus]     = useState<Status | 'all'>('all');
  const [recentSlugs, setRecentSlugs]       = useState<string[]>(() => storage.getRecentSlugs());

  const ss = useStudioState();

  // Auto-seed createdAt on first encounter (localStorage only — not shared state)
  useEffect(() => {
    if (!ss.ready) return;
    const today = new Date().toISOString().slice(0, 10);
    for (const p of prototypes) {
      if (p.meta.createdAt) continue;
      if (storage.getCreated(p.slug)) continue;
      storage.setCreated(p.slug, today);
    }
  }, [prototypes, ss.ready]);

  // Resolve effective fields per prototype
  const resolved = useMemo(() => {
    void ss.version; // déclenche le recalcul après chaque mutation (y compris fallback localStorage)
    if (!ss.ready) return [];
    return prototypes.map((p) => {
      const isDeleted  = ss.isDeleted(p.slug);
      const codeArchived = p.meta.status === 'archived' && !ss.isUnarchived(p.slug);
      const isArchived = !isDeleted && (codeArchived || ss.isArchived(p.slug));
      const category   = isDeleted
        ? 'corbeille'
        : isArchived
          ? 'archive'
          : (normalizeCategory(ss.getCategory(p.slug))
            ?? normalizeCategory(p.meta.category)
            ?? autoCategoryFromMeta(p.meta));
      const createdAt  = p.meta.createdAt ?? storage.getCreated(p.slug) ?? null;
      const title      = ss.getTitle(p.slug) ?? p.meta.title;
      const override   = ss.getStatus(p.slug);
      const codeStatus: Status = p.meta.status === 'archived' && !codeArchived ? 'draft' : (p.meta.status ?? 'draft');
      const status: Status = isEditableStatus(override) ? override : codeStatus;
      return { proto: p, category, createdAt, isArchived, isDeleted, title, status };
    });
  }, [prototypes, ss, ss.version]);

  // Stats for hero
  const activeCount   = resolved.filter((r) => !r.isArchived && !r.isDeleted).length;
  const archivedCount = resolved.filter((r) => r.isArchived).length;
  const categoryCount = useMemo(() => {
    const cats = new Set(resolved.filter((r) => !r.isArchived && !r.isDeleted).map((r) => pageKeyOf(r.category)));
    return cats.size;
  }, [resolved]);

  // Authors + tags for filter panel
  const authors = useMemo(() =>
    [...new Set(prototypes.map((p) => p.meta.author).filter((a): a is string => !!a))].sort(),
    [prototypes]
  );
  const availableTags = useMemo(() =>
    [...new Set(prototypes.flatMap((p) => p.meta.tags ?? []))].sort(),
    [prototypes]
  );

  // Filter + sort
  const visible = useMemo(() => {
    let items = [...resolved];

    if (activeStatus !== 'all') {
      items = items.filter(({ status, isArchived, isDeleted }) =>
        !isArchived && !isDeleted && status === activeStatus
      );
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(({ proto, title }) =>
        title.toLowerCase().includes(q) ||
        proto.meta.description?.toLowerCase().includes(q) ||
        proto.meta.author?.toLowerCase().includes(q) ||
        proto.slug.toLowerCase().includes(q) ||
        proto.meta.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (activeAuthors.length > 0) {
      items = items.filter(({ proto }) => proto.meta.author && activeAuthors.includes(proto.meta.author));
    }

    if (activeTags.length > 0) {
      items = items.filter(({ proto }) =>
        activeTags.every((t) => proto.meta.tags?.includes(t))
      );
    }

    if (activeCategory !== null) {
      items = items.filter(({ category }) => matchesCategoryFilter(category, activeCategory));
    }

    items.sort((a, b) => {
      if (sort === 'alpha') return a.title.localeCompare(b.title, 'fr');
      const da = a.createdAt ?? '0000-00-00';
      const db = b.createdAt ?? '0000-00-00';
      return sort === 'recent' ? db.localeCompare(da) : da.localeCompare(db);
    });

    return items;
  }, [resolved, search, activeAuthors, activeTags, activeCategory, activeStatus, sort]);

  // Group by page, then sub-page
  const grouped = useMemo(() => {
    const map: Record<string, PageGroup<(typeof visible)[number]>> = {};
    for (const cat of CATEGORIES) map[cat.key] = { total: 0, direct: [], subpages: {} };
    for (const item of visible) {
      const group = map[pageKeyOf(item.category)] ?? map.autres;
      const sub = subpageKeyOf(item.category);
      group.total += 1;
      if (sub) (group.subpages[sub] ??= []).push(item);
      else group.direct.push(item);
    }
    return map;
  }, [visible]);

  // Sidebar counts
  const catCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const { category } of resolved.filter((r) => !r.isArchived && !r.isDeleted)) {
      c[category] = (c[category] ?? 0) + 1;
    }
    return c;
  }, [resolved]);
  const trashCount = useMemo(() => resolved.filter((r) => r.isDeleted).length, [resolved]);

  // Mutations
  const onChangeCategory = async (slug: string, newCat: string) => {
    if (newCat === 'archive') { await ss.setArchived(slug, true); await ss.setCategory(slug, null); }
    else { await ss.setArchived(slug, false); await ss.setCategory(slug, newCat); }
  };
  const onArchive         = (slug: string) => ss.setArchived(slug, true).then(() => ss.setCategory(slug, null));
  const onUnarchive       = (slug: string) => ss.setArchived(slug, false);
  const onDelete          = (slug: string) => ss.setDeleted(slug, true);
  const onRestore         = (slug: string) => ss.setDeleted(slug, false);
  const onChangeTitle     = (slug: string, v: string) => ss.setTitle(slug, v);
  const onChangeStatus    = (slug: string, v: Status) => {
    const original = prototypes.find((p) => p.slug === slug)?.meta.status ?? 'draft';
    return ss.setStatus(slug, v === original ? null : v);
  };
  const onCardClick       = (slug: string) => { storage.pushRecentSlug(slug); setRecentSlugs(storage.getRecentSlugs()); };

  const onToggleAuthor = (a: string) =>
    setActiveAuthors((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);
  const onToggleTag = (t: string) =>
    setActiveTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  const activeFiltersCount = activeAuthors.length + activeTags.length + (activeStatus === 'all' ? 0 : 1);
  const onClearAll = () => { setActiveAuthors([]); setActiveTags([]); setActiveStatus('all'); setSearch(''); };

  const hasActiveFilters = !!(search || activeFiltersCount > 0 || activeCategory);

  // Sans filtre, les pages réseau restent visibles même vides pour montrer les références à construire.
  const showEmptyFolders = !hasActiveFilters;
  const categoriesToRender = CATEGORIES.filter((cat) => {
    if (activeCategory !== null) return matchesCategoryFilter(cat.key, activeCategory);
    return grouped[cat.key]?.total > 0 || (showEmptyFolders && NETWORK_PAGE_KEYS.includes(cat.key));
  });

  const renderCards = (items: (typeof visible)) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
      {items.map(({ proto, category, createdAt, title, status, isArchived, isDeleted }) => (
        <PrototypeCard
          key={proto.slug}
          proto={proto}
          title={title}
          status={status}
          onChangeStatus={onChangeStatus}
          category={category}
          createdAt={createdAt}
          isArchived={isArchived}
          isDeleted={isDeleted}
          onChangeCategory={onChangeCategory}
          protoBasePath="/size2mix/prototype"
          onChangeTitle={onChangeTitle}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onDelete={onDelete}
          onRestore={onRestore}
          onClick={() => onCardClick(proto.slug)}
        />
      ))}
    </div>
  );

  const networkPages = categoriesToRender.filter((cat) => NETWORK_PAGE_KEYS.includes(cat.key));
  const otherPages = categoriesToRender.filter((cat) => !NETWORK_PAGE_KEYS.includes(cat.key));
  const networkTotal = networkPages.reduce((sum, cat) => sum + (grouped[cat.key]?.total ?? 0), 0);

  const renderPage = (cat: CategoryDef) => {
    const group = grouped[cat.key];
    const activeSub = activeCategory?.includes('/') ? subpageKeyOf(activeCategory) : null;
    const subpages = (cat.subpages ?? []).filter((sub) =>
      activeSub ? sub.key === activeSub : (group.subpages[sub.key]?.length ?? 0) > 0 || showEmptyFolders
    );
    return (
      <NJAccordionItem
        key={cat.key}
        data-page={cat.key}
        scale="lg"
        open={group.total > 0 || activeCategory !== null}
      >
        <NJAccordionItemHeader>
          <FolderTitle cat={cat} count={group.total} />
        </NJAccordionItemHeader>
        <NJAccordionItemContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--nj-semantic-size-spacing-16)' }}>
            {subpages.length > 0 && (
              <NJAccordion>
                {subpages.map((sub) => {
                  const items = group.subpages[sub.key] ?? [];
                  return (
                    <NJAccordionItem key={sub.key} data-subpage={`${cat.key}/${sub.key}`} open={items.length > 0}>
                      <NJAccordionItemHeader>
                        <FolderTitle label={sub.label} icon={items.length > 0 ? 'folder_open' : 'folder'} count={items.length} />
                      </NJAccordionItemHeader>
                      <NJAccordionItemContent>
                        {items.length > 0 ? renderCards(items) : <EmptyFolder />}
                      </NJAccordionItemContent>
                    </NJAccordionItem>
                  );
                })}
              </NJAccordion>
            )}
            {group.direct.length > 0 && (
              <div>
                {cat.subpages && (
                  <p style={{ margin: '0 0 var(--nj-semantic-size-spacing-12)', fontSize: 13, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                    Sans sous-page
                  </p>
                )}
                {renderCards(group.direct)}
              </div>
            )}
            {group.total === 0 && !cat.subpages && <EmptyFolder />}
          </div>
        </NJAccordionItemContent>
      </NJAccordionItem>
    );
  };

  if (!ss.ready) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: S.bgDeep }}>
        <span style={{ color: S.textMuted, fontSize: 14 }}>Chargement…</span>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: S.bgDeep, fontFamily: 'var(--nj-core-font-family-sans, system-ui, sans-serif)' }}>
      <HeroSection
        total={prototypes.length}
        activeCount={activeCount}
        archivedCount={archivedCount}
        categoryCount={categoryCount}
        sort={sort}
        onSort={setSort}
        search={search}
        onSearch={setSearch}
        authors={authors}
        activeAuthors={activeAuthors}
        onToggleAuthor={onToggleAuthor}
        availableTags={availableTags}
        activeTags={activeTags}
        onToggleTag={onToggleTag}
        activeFiltersCount={activeFiltersCount}
        onClearAll={onClearAll}
      />

      <div
        role="group"
        aria-label="Filtrer par statut"
        style={{
          display: 'flex', flexWrap: 'wrap',
          gap: 'var(--nj-semantic-size-spacing-8)',
          padding: 'var(--nj-semantic-size-spacing-16) var(--nj-semantic-size-spacing-32)',
          background: 'var(--nj-core-color-grey-100)',
        }}
      >
        {STATUS_FILTERS.map(({ key, label }) => (
          <NJButton
            key={key}
            label={label}
            scale="sm"
            variant="secondary"
            emphasis={activeStatus === key ? 'bold' : 'minimal'}
            aria-pressed={activeStatus === key}
            onClick={() => setActiveStatus(key)}
          />
        ))}
      </div>

      <RecentBar
        prototypes={activeStatus === 'all' ? prototypes : visible.map(({ proto }) => proto)}
        recentSlugs={recentSlugs}
        protoBasePath="/size2mix/prototype"
      />

      {hasActiveFilters && (
        <div style={{
          padding: '8px 32px',
          background: '#f1f5f9',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          fontSize: 12,
        }}>
          <span style={{ color: '#64748b', fontWeight: 500 }}>
            {visible.length} résultat{visible.length !== 1 ? 's' : ''}
          </span>
          {search && <ActiveChip label={`"${search}"`} onRemove={() => setSearch('')} />}
          {activeStatus !== 'all' && (
            <ActiveChip
              label={STATUS_FILTERS.find((s) => s.key === activeStatus)?.label ?? activeStatus}
              onRemove={() => setActiveStatus('all')}
            />
          )}
          {activeCategory && (
            <ActiveChip
              label={getCategoryDef(activeCategory)?.label ?? activeCategory}
              onRemove={() => setActiveCategory(null)}
            />
          )}
          {activeAuthors.map((a) => (
            <ActiveChip key={a} label={`@${a.split('.')[0]}`} onRemove={() => onToggleAuthor(a)} />
          ))}
          {activeTags.map((t) => (
            <ActiveChip key={t} label={t} onRemove={() => onToggleTag(t)} />
          ))}
          <button type="button" onClick={onClearAll} style={{
            marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', fontSize: 12, fontFamily: 'inherit',
          }}>
            Tout effacer
          </button>
        </div>
      )}

      <div style={{ display: 'flex', minHeight: 'calc(100vh - 280px)', background: '#f1f5f9' }}>
        <CategorySidebar
          counts={catCounts}
          trashCount={trashCount}
          activeCategory={activeCategory}
          onSelect={setActiveCategory}
        />

        <main style={{ flex: 1, padding: '28px 32px', minWidth: 0 }}>
          {visible.length === 0 ? (
            <EmptyState search={search} onClear={onClearAll} />
          ) : (
            <NJAccordion isSeparated>
              {networkPages.length > 0 && (
                <NJAccordionItem key="network" data-group="network" scale="lg" open>
                  <NJAccordionItemHeader>
                    <FolderTitle cat={{ key: NETWORK_GROUP_KEY, ...NETWORK_GROUP }} count={networkTotal} />
                  </NJAccordionItemHeader>
                  <NJAccordionItemContent>
                    <NJAccordion isSeparated>{networkPages.map(renderPage)}</NJAccordion>
                  </NJAccordionItemContent>
                </NJAccordionItem>
              )}
              {otherPages.map(renderPage)}
            </NJAccordion>
          )}
        </main>
      </div>
    </div>
  );
}

interface PageGroup<T> {
  total: number;
  direct: T[];
  subpages: Record<string, T[]>;
}

function FolderTitle({ cat, label, icon, count }: { cat?: CategoryDef; label?: string; icon?: string; count: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--nj-semantic-size-spacing-8)' }}>
      <span
        className="material-symbols-outlined"
        aria-hidden="true"
        style={{ fontSize: 18, color: cat?.color ?? 'var(--nj-semantic-color-icon-neutral-secondary-default)' }}
      >
        {cat?.icon ?? icon ?? 'folder'}
      </span>
      <span>{cat?.label ?? label}</span>
      <span style={{
        fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 8,
        color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
        background: 'var(--nj-core-color-grey-100)',
      }}>
        {count}
      </span>
    </span>
  );
}

function EmptyFolder() {
  return (
    <p style={{ margin: 0, fontSize: 13, color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
      Aucun prototype — référence à construire.
    </p>
  );
}

function ActiveChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: S.accentBadge, color: S.accentText,
      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500,
      border: `1px solid ${S.accent}40`,
    }}>
      {label}
      <button type="button" onClick={onRemove} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: S.accentText, fontSize: 13, lineHeight: 1, padding: 0,
      }}>×</button>
    </span>
  );
}

function EmptyState({ search, onClear }: { search: string; onClear: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 24px', color: S.textMuted }}>
      <span className="material-symbols-outlined" style={{ fontSize: 48, display: 'block', marginBottom: 16, opacity: 0.3 }}>
        search_off
      </span>
      <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: S.textMuted }}>Aucun prototype trouvé</p>
      <p style={{ margin: '6px 0 16px', fontSize: 13, color: S.textDim }}>
        {search ? `Aucun résultat pour "${search}"` : 'Aucun prototype ne correspond aux filtres actifs.'}
      </p>
      <button type="button" onClick={onClear} style={{
        padding: '8px 20px', borderRadius: 8,
        border: `1px solid ${S.border}`,
        background: S.bgHover, cursor: 'pointer',
        fontSize: 13, fontFamily: 'inherit', color: S.textPrimary,
      }}>
        Effacer les filtres
      </button>
    </div>
  );
}
